<script setup>
import {computed, onBeforeMount, watch, ref, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useTheme } from '@/composables/useTheme'
import { getSafeRedirectPath } from '@/utils/auth/validation'
import WsHandler from '@/services/wsHandler.vue'

import Toaster from '@/components/ui/toast/Toaster.vue'
import {useToast} from '@/components/ui/toast/use-toast'
import { AppNavigation } from '@/components/navigation'
import { Footer } from '@/components/footer'
import { MainMap, MapSettings, Navigator } from '@/components/map'
import { LayoutDialog, LayoutSidebar } from "@/layouts"
import { SelectedFilters } from '@/components/filters'
import { Breadcrumbs } from '@/components/breadcrumbs'
import {SplitPane} from '@/components/splitpane'
import {ProjectList} from '@/components/project-list'
import { useActiveTab } from '@/composables/useActiveTab'
import {OrgLayers} from '@/components/layers'
import {AIChat} from '@/components/reports'
import {Reports} from '@/components/report'
import OrgPaymentsPanel from '@/components/payments/OrgPaymentsPanel.vue'
import { Spinner } from '@/components/ui/spinner'
import ChatReportSidebar from '@/components/chat/reports/ChatReportSidebar.vue'
import { SidebarPanel } from '@/components/ui/sidebar-panel'
import KYCPromptDialog from '@/components/bridge/KYCPromptDialog.vue'
const { toast } = useToast()
const { activeTab } = useActiveTab()

const router = useRouter()
const route = useRoute()
const store = useStore()
const { initAuthListener, getIdToken } = useAuth()
useTheme()
const currentRoute = computed(() => route.name)

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const member = computed(() => store.getters['user/getMember'])

const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const isLoadingData = computed(() => store.getters.isLoadingData)
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const showBackgroundUI = computed(() => route.meta.showBackgroundUI !== false)
const hasDates = computed(() => {
  const dates = store.getters['layers/getDates']
  return dates && dates.length > 0
})

const isDragging = ref(false)
const isContentFullWidth = ref(false)

const toggleContentFullWidth = () => {
  isContentFullWidth.value = !isContentFullWidth.value
}

// Chat report sidebar - shown when on ChatSession route with report query param
const showChatReportSidebar = computed(() => {
  return route.name === 'ChatSession' && route.query.report
})

const isReportFullWidth = ref(false)

const toggleReportFullWidth = () => {
  isReportFullWidth.value = !isReportFullWidth.value
}

const closeChatReport = () => {
  router.push({
    name: 'ChatSession',
    params: route.params,
    query: { ...route.query, report: undefined }
  })
}


onBeforeMount(async () => {
  initAuthListener(async (user) => {
    if (route.name === 'Invite') {
      return
    }

    if (user) {
      await store.dispatch('user/setupSettings')
      await store.dispatch('user/setUser', user)

      try {
        const firebaseToken = await getIdToken(true)
        store.commit('SET_FIREBASE_TOKEN', firebaseToken)
        const result = await store.dispatch('user/handleAuthentication', {
          firebaseToken
        })

        if (result.success) {
          const redirectPath = getSafeRedirectPath(route.query.redirect)
          if (redirectPath) {
            router.push(redirectPath)
            return
          }

          await store.dispatch('org/fetchOrgs')

          if (!currentRoute.value.startsWith('Auth')) return
          router.push({name: 'AuthOverview'})
        } else {
          if (result.error === 'USER_NOT_REGISTERED') {
            const redirectPath = getSafeRedirectPath(route.query.redirect)
            if (redirectPath) {
              router.push(redirectPath)
              return
            }
            // Stay on sign-up page if user is already there
            if (route.name === 'SignUp') return
          }
          router.push({name: 'AuthOverview'})
        }
      } catch (e) {
        logOut()
      }
    } else {
      logOut()
    }
  })
})

const appHeight = () => document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)

onMounted(async () => {
  await router.isReady()
  store.commit('search/SET_PARAMS', route.query)

  window.addEventListener('resize', appHeight)
  appHeight()
})

watch(
  () => route.params.org_id,
  (newOrgId) => {
    if (newOrgId && newOrgId.toString() !== selectedOrgId.value?.toString()) {
      store.dispatch('org/setSelectedOrg', newOrgId.toString())
    }
  },
  { immediate: true }
)

watch(
  () => route.query,
  (newQuery) => {
    if (Object.keys(newQuery).length > 0) {
      store.commit('search/SET_PARAMS', newQuery)
    }
  },
  { deep: true }
)

watch(
  () => member.value,
  async (value) => {
    if (value) {
      if (!zE) return
      zE('webWidget', 'identify', {
        name: value.display_name ? value.display_name : value.email,
        email: value.email
      })
    }
  }
)

const logOut = () => {
  store.dispatch('logout')
}

const handleDrop = (e) => {
  e.preventDefault()
  e.stopPropagation()

  isDragging.value = false

  const file = e.dataTransfer.files[0]

  if (!file) {
    return
  }

  const ext = file.name.split('.').pop().toLowerCase()
  if (!['kml', 'kmz', 'geojson', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'avif', 'webp', 'gpx', 'fit'].includes(ext)) {
    toast({ title: "Unsupported file format", variant: 'destructive' })
    return
  }

  store.commit('geo/SET_FILES', e.dataTransfer.files)

  if (route.name !== 'FileViewer') {
    const orgId = route.params.org_id || store.getters['org/getSelected']
    router.push({name: 'FileViewer', params: {org_id: orgId}})
  }
}
</script>

<template>
<div class="flex h-screen">
  <!-- Sidebar (only when logged in with background UI) -->
  <AppNavigation v-if="isLoggedIn && showBackgroundUI" />

  <div class="flex-1 flex flex-col min-w-0">
    <ws-handler v-if="isLoggedIn && showBackgroundUI" />

    <main class="flex-1 z-40 text-sm main-section relative" role="main" aria-label="Main application content" id="main-content" :class="{ 'no-background-ui': !showBackgroundUI }">
      <!-- Fullscreen layout: header + content + footer, no map/sidebar -->
      <div v-if="$route.meta.layout === 'fullscreen'" class="h-full w-full overflow-auto">
        <router-view />
      </div>

      <!-- Payments: full-width, no map -->
      <div v-else-if="showBackgroundUI && activeTab === 'payments'" class="h-full w-full bg-background flex flex-col overflow-hidden">
        <OrgPaymentsPanel />
      </div>

      <!-- Background UI with Left Panel and Map -->
      <SplitPane v-else-if="showBackgroundUI">
        <template v-slot:left>
          <nav class="min-w-[330px] bg-background h-full flex flex-col overflow-hidden pt-2" role="navigation" aria-label="Projects and layers navigation">
            <div class="flex-1 overflow-y-auto" v-if="activeTab === 'projects'">
              <ProjectList />
            </div>
            <div class="flex-1 overflow-y-auto" v-else-if="activeTab === 'layers'">
              <OrgLayers :key="activeTab" />
            </div>
            <div class="flex-1 flex flex-col overflow-hidden" v-else-if="activeTab === 'chats'">
              <AIChat :key="activeTab" />
            </div>
            <div class="flex-1 flex flex-col overflow-hidden" v-else-if="activeTab === 'reports'">
              <Reports :key="activeTab" />
            </div>
          </nav>
        </template>

        <template v-slot:right>
          <div
            class="w-full h-full flex flex-col relative bg-muted"
            @dragenter="isDragging = true"
            role="region"
            aria-label="Map and content area"
          >
            <div class="w-full h-full relative">
              <MainMap :active-tab="activeTab" v-if="isLoggedIn" />
              <div
                v-if="isLoadingData"
                class="absolute z-50 top-14 bg-background rounded-full p-2 shadow-lg"
                style="left: 50%"
                role="status"
                aria-live="polite"
                aria-label="Loading data"
                data-testid="loading-indicator"
              >
                <Spinner class="w-6 h-6" aria-hidden="true" />
                <span class="sr-only">Loading data...</span>
              </div>

              <div
                v-if="isLoggedIn && isDragging"
                @dragover.prevent
                @drop.stop.prevent="handleDrop"
                @dragleave="isDragging = false"
                class="absolute inset-0 z-10 bg-gray-600 opacity-20"
                role="region"
                aria-label="File drop zone"
                aria-describedby="drop-zone-description"
              >
                <span id="drop-zone-description" class="sr-only">
                  Drop supported files here (KML, KMZ, GeoJSON, images, GPX, FIT)
                </span>
              </div>
            </div>

            <div class="absolute inset-0 z-20 pointer-events-none">
              <div class="h-full w-full flex">
                <div
                  class="h-full flex flex-col p-2 transition-all duration-300"
                  :class="isContentFullWidth ? 'flex-1' : 'w-2/3'"
                >
                  <div v-if="isLoggedIn">
                    <Breadcrumbs class="mb-2 pointer-events-auto" />
                  </div>

                  <div
                    class="flex-1 overflow-hidden rounded-md"
                    role="region"
                    aria-label="Content area"
                  >
                    <div class="h-full">
                      <!-- Chat Report Sidebar -->
                      <SidebarPanel
                        v-if="showChatReportSidebar"
                        title="Report"
                        layout="float-big"
                        :is-full-width="isReportFullWidth"
                        :show="true"
                        @toggle-full-width="toggleReportFullWidth"
                        @close="closeChatReport"
                      >
                        <ChatReportSidebar />
                      </SidebarPanel>
                      <LayoutDialog v-if="$route.meta.layout === 'modal'" />
                      <div v-else-if="$route.meta.layout === 'hide'"></div>
                      <LayoutSidebar
                        v-else
                        :is-full-width="isContentFullWidth"
                        @toggle-full-width="toggleContentFullWidth"
                      />
                    </div>
                  </div>

                  <div
                    v-if="isLoggedIn && hasDates"
                    class="pointer-events-auto w-full mt-2"
                    role="navigation"
                    aria-label="Map navigation controls"
                  >
                    <Navigator />
                  </div>
                </div>

                <div
                  class="h-full p-2 transition-all duration-300"
                  :class="{
                    'w-[5%]': isContentFullWidth || isReportFullWidth,
                    'w-1/3 flex-1': !isContentFullWidth && !isReportFullWidth
                  }"
                >
                  <!-- Map controls (shown when no report sidebar) -->
                  <div
                    class="flex flex-col items-end pointer-events-auto"
                    role="complementary"
                    aria-label="Map controls and filters"
                  >
                    <div
                      class="mb-1"
                      v-if="projectId"
                      role="group"
                      aria-label="Active filters"
                    >
                      <SelectedFilters />
                    </div>
                    <div role="group" aria-label="Map settings">
                      <MapSettings />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </SplitPane>

      <!-- Direct router view when background UI is disabled -->
      <div v-else class="h-full w-full">
        <LayoutDialog v-if="$route.meta.layout === 'modal'" />
        <div v-else-if="$route.meta.layout === 'hide'"></div>
        <router-view v-else-if="$route.meta.layout === 'fullscreen'" />
        <LayoutSidebar v-else />
      </div>
    </main>

    <footer
      class="z-40 h-[50px] shadow-lg hidden lg:block"
      role="contentinfo"
      aria-label="Application footer"
    >
      <Footer />
    </footer>
  </div>
</div>
<Toaster />
<KYCPromptDialog />
</template>

<style>
.main-section {
  max-height: var(--app-height);
  min-height: var(--app-height);

  @media (min-width: 1024px) {
    max-height: calc(var(--app-height) - 50px);
    min-height: calc(var(--app-height) - 50px);
  }
}

.main-section.no-background-ui {
  max-height: var(--app-height);
  min-height: var(--app-height);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
